// pipe scan-intake (track D): a PURE ZPL II builder for Zebra thermal label printers — no React,
// no DOM (the Blob download lives in label-sheet.tsx). Targets a 2.25" × 1.25" label at 203 dpi
// and uses the printer's NATIVE ^BC (Code 128) encoder, so the firmware handles subset selection
// and checksum; the sibling code128.ts encoder exists for the on-screen/print-sheet SVG only.

import { assertCode128Encodable } from './code128';

/** One queued label: the scannable code plus optional human-readable context lines. */
export interface LabelItem {
  /** Barcode payload (entity code / serial) — printable ASCII (32–126), same range as code128.ts. */
  code: string;
  /** Optional headline (e.g. entity title); wrapped by ^FB, truncated after 2 lines. */
  title?: string;
  /** Optional secondary line (e.g. customer / order ref); wrapped by ^FB, truncated after 2 lines. */
  subtitle?: string;
}

// Physical label geometry: 2.25in × 1.25in stock on a 203 dpi head → 457 × 254 dots.
export const LABEL_DPI = 203;
export const LABEL_WIDTH_DOTS = 457;
export const LABEL_HEIGHT_DOTS = 254;

// Layout (dots). Vertical budget: title 8+2×22≈52 → barcode 58..166 → code line 176..200 →
// subtitle 204..244, inside the 254-dot label. ^FB blocks are hard-capped in line count, so an
// over-long title/subtitle truncates instead of overflowing into the barcode.
const MARGIN = 12;
const TEXT_WIDTH = LABEL_WIDTH_DOTS - 2 * MARGIN; // 433
const TITLE_Y = 8;
const BARCODE_Y = 58;
const BARCODE_HEIGHT = 108;
const CODE_LINE_Y = 176;
const SUBTITLE_Y = 204;

/** Hex-escape `text` for a `^FH_`-prefixed ^FD field: the ZPL command/control characters
 *  (`^ ~ \ _`) and ASCII control chars become `_hh`, so field data can never terminate or inject a
 *  command. Non-ASCII text passes through raw — every label runs under ^CI28 (UTF-8). */
export function zplFieldData(text: string): string {
  let out = '';
  for (const ch of text) {
    const cc = ch.codePointAt(0) ?? 0;
    out +=
      cc < 0x20 ||
      cc === 0x7f ||
      ch === '^' ||
      ch === '~' ||
      ch === '\\' ||
      ch === '_'
        ? `_${cc.toString(16).padStart(2, '0')}`
        : ch;
  }
  return out;
}

/** A ^FH_-escaped text field wrapped in a ^FB block (`lines` max — wrap, never overflow). */
const textField = (
  y: number,
  fontDots: number,
  lines: number,
  justify: 'L' | 'C',
  text: string
): string =>
  `^FO${MARGIN},${y}^A0N,${fontDots},${fontDots}` +
  `^FB${TEXT_WIDTH},${lines},0,${justify},0^FH_^FD${zplFieldData(text)}^FS`;

/** Build one complete ^XA…^XZ label. The barcode is native ^BCN (Code 128, auto subset, no
 *  firmware interpretation line — we print our own centred human-readable line instead). */
export function labelZpl({ code, title, subtitle }: LabelItem): string {
  assertCode128Encodable(code);
  const lines = [
    '^XA',
    '^CI28', // UTF-8 field data (recipient-locale titles — i18n rule)
    `^PW${LABEL_WIDTH_DOTS}`,
    `^LL${LABEL_HEIGHT_DOTS}`,
    '^LH0,0',
  ];
  if (title) {
    lines.push(textField(TITLE_Y, 22, 2, 'L', title));
  }
  // ^BY2 = 2-dot narrow module (the 203 dpi floor for reliable scans).
  lines.push(
    `^FO${MARGIN},${BARCODE_Y}^BY2^BCN,${BARCODE_HEIGHT},N,N,N^FH_^FD${zplFieldData(code)}^FS`
  );
  lines.push(textField(CODE_LINE_Y, 24, 1, 'C', code));
  if (subtitle) {
    lines.push(textField(SUBTITLE_Y, 20, 2, 'L', subtitle));
  }
  lines.push('^XZ');
  return lines.join('\n');
}

/** The whole queue as one printable stream — each item is its own ^XA…^XZ label. */
export function zplForQueue(items: readonly LabelItem[]): string {
  return items.map(labelZpl).join('\n');
}
